/* ==========================================================================
   Lovelle — cosy.js (Lovelle Cosy stroller mittens, product.cosy.json)
   Loaded only on that template, after bra.js (both deferred, in that order).
   bra.js already owns the packs, the per-pair colour <select>s, the
   add-to-cart, reviews and the lightbox. This adds:

   1. The colour block above the packs (the Dab & Go selector): a swatch
      puts its colour on every pair, "different colours per pair" opens a
      colour row per pair; pair N stays the same in every pack.
   2. The sticky bar follows the chosen pack (name + total) and adds it.
   3. Colour cards further down (the card's button puts that colour on every
      pair and shows its photo), the numbered detail photo and in-page links
      under the sticky header (stars under the title lead to the reviews).

   The size helpers from the sock page it was built from stay dormant: this
   product has no size, so no size row is ever rendered.
   No user-facing string is written here; every word is already in the DOM.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-cs-root]');
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function headerOffset() {
    var h = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-h'), 10);
    return (isNaN(h) ? 64 : h) + 14;
  }
  function scrollToEl(el) {
    if (!el) return;
    var y = el.getBoundingClientRect().top + window.pageYOffset - headerOffset();
    window.scrollTo({ top: y, behavior: reduced ? 'auto' : 'smooth' });
  }
  function flash(el) {
    if (!el) return;
    el.classList.remove('is-flash');
    void el.offsetWidth;
    el.classList.add('is-flash');
  }

  /* ---- 7: in-page links (stars under the title, "see all reviews") ---- */
  qsa('[data-cs-scroll]').forEach(function (a) {
    a.addEventListener('click', function (e) {
      var target = qs(a.getAttribute('data-cs-scroll'));
      if (!target) return;
      e.preventDefault();
      scrollToEl(target);
      if (history.replaceState) history.replaceState(null, '', a.getAttribute('data-cs-scroll'));
    });
  });

  /* ---- 7: photo with numbered details ---- */
  qsa('[data-cs-spots]').forEach(function (box) {
    var dots = qsa('[data-cs-spot]', box);
    var panels = qsa('[data-cs-spot-panel]', box);
    if (!dots.length) return;
    function show(i) {
      dots.forEach(function (d, n) { d.classList.toggle('is-active', n === i); d.setAttribute('aria-pressed', n === i ? 'true' : 'false'); });
      panels.forEach(function (p, n) { p.classList.toggle('is-active', n === i); });
    }
    dots.forEach(function (d, n) { d.addEventListener('click', function () { show(n); }); });
    box.classList.add('is-ready');

    /* Desktop with a mouse: the explanations stay hidden until a number is
       pointed at (or focused with the keyboard); the card then opens beside
       that number and closes when the pointer leaves. Phones keep tap-to-show. */
    var mq = window.matchMedia ? window.matchMedia('(min-width: 961px) and (hover: hover)') : null;
    function desktop() { return !!(mq && mq.matches); }
    var hideTimer = null;
    function peek(n) {
      if (!desktop()) return;
      clearTimeout(hideTimer);
      show(n);
      box.classList.add('is-peek');
    }
    function unpeek() {
      if (!desktop()) return;
      clearTimeout(hideTimer);
      hideTimer = setTimeout(function () {
        box.classList.remove('is-peek');
        dots.forEach(function (d) { d.classList.remove('is-active'); d.setAttribute('aria-pressed', 'false'); });
      }, 80);
    }
    function syncMode() {
      box.classList.toggle('is-hover-mode', desktop());
      if (desktop()) { box.classList.remove('is-peek'); dots.forEach(function (d) { d.classList.remove('is-active'); d.setAttribute('aria-pressed', 'false'); }); }
      else show(0);
    }
    dots.forEach(function (d, n) {
      d.addEventListener('mouseenter', function () { peek(n); });
      d.addEventListener('mouseleave', unpeek);
      d.addEventListener('focus', function () { peek(n); });
      d.addEventListener('blur', unpeek);
    });
    if (mq) { if (mq.addEventListener) mq.addEventListener('change', syncMode); else if (mq.addListener) mq.addListener(syncMode); }
    syncMode();
  });

  /* ---- 6: size guide dialog + calculator (works on every section) ---- */
  var guide = qs('[data-cs-guide]');
  var lastOpener = null;
  function openGuide(opener) {
    if (!guide) return;
    lastOpener = opener || null;
    if (typeof guide.showModal === 'function') { if (!guide.open) guide.showModal(); }
    else guide.setAttribute('open', '');
    document.documentElement.classList.add('cs-guide-open');
  }
  function closeGuide() {
    if (!guide) return;
    guide.classList.add('is-closing');
    setTimeout(function () {
      guide.classList.remove('is-closing');
      if (typeof guide.close === 'function' && guide.open) guide.close();
      else guide.removeAttribute('open');
      document.documentElement.classList.remove('cs-guide-open');
      if (lastOpener && lastOpener.focus) lastOpener.focus({ preventScroll: true });
    }, reduced ? 0 : 200);
  }
  qsa('[data-cs-guide-open]').forEach(function (b) {
    b.addEventListener('click', function (e) { e.preventDefault(); openGuide(b); });
  });
  if (guide) {
    qsa('[data-cs-guide-close]', guide).forEach(function (b) { b.addEventListener('click', closeGuide); });
    guide.addEventListener('click', function (e) { if (e.target === guide) closeGuide(); });
    guide.addEventListener('cancel', function (e) { e.preventDefault(); closeGuide(); });
  }

  /* The chart (snippets/cosy-size-chart), by shoe size, the larger size when
     in between: EU under 38 S/M · 38–41½ L/XL · 42+ 2XL; UK (any number under
     20) under 5 S/M · 5–7 L/XL · 7½+ 2XL. */
  function sizeFor(shoe) {
    if (!shoe) return null;
    if (shoe < 20) {
      if (shoe < 1 || shoe > 13) return null;
      return shoe < 5 ? 'S/M' : shoe < 7.5 ? 'L/XL' : '2XL';
    }
    if (shoe < 33 || shoe > 48) return null;
    return shoe < 38 ? 'S/M' : shoe < 42 ? 'L/XL' : '2XL';
  }
  qsa('[data-cs-calc]').forEach(function (form) {
    var shoe = qs('[data-cs-shoe]', form);
    var res = qs('[data-cs-calc-res]', form);
    var sizeEl = qs('[data-cs-calc-size]', form);
    var use = qs('[data-cs-calc-use]', form);
    var msg = qs('[data-cs-calc-msg]', form);
    var chart = form.parentNode;
    var current = null;
    function mark(s) {
      qsa('[data-cs-row]', chart).forEach(function (r) { r.classList.toggle('is-match', r.getAttribute('data-cs-row') === s); });
    }
    function run() {
      var b = parseFloat(String(shoe.value).replace(',', '.'));
      current = null;
      if (!b) {
        res.hidden = true; use.hidden = true; mark(null);
        msg.textContent = shoe.value ? form.getAttribute('data-msg-empty') : '';
        msg.hidden = !msg.textContent;
        return;
      }
      var s = sizeFor(b);
      if (!s) {
        res.hidden = true; use.hidden = true; mark(null);
        msg.textContent = form.getAttribute('data-msg-out');
        msg.hidden = false;
        return;
      }
      current = s;
      msg.hidden = true;
      sizeEl.textContent = s;
      res.hidden = false;
      res.classList.remove('is-new'); void res.offsetWidth; res.classList.add('is-new');
      mark(s);
      if (window.CosySetSize) {
        use.textContent = (form.getAttribute('data-use') || '').replace('[size]', s);
        use.hidden = false;
      }
    }
    form.addEventListener('input', run);
    form.addEventListener('submit', function (e) { e.preventDefault(); run(); });
    use.addEventListener('click', function () {
      if (!current || !window.CosySetSize) return;
      window.CosySetSize(current);
      if (guide && guide.contains(form)) closeGuide();
      setTimeout(function () {
        var block = qs('[data-cs-bundle] .is-selected') || qs('[data-cs-bundle]');
        scrollToEl(block);
        flash(block);
      }, reduced ? 0 : 220);
    });
  });

  /* ---- 7: All / Solid / Pattern tabs over the design cards (no buy box needed) ---- */
  qsa('[data-cs-colours]').forEach(function (sec) {
    var tabs = qsa('[data-cs-group]', sec);
    var cards = qsa('[data-cs-card]', sec);
    tabs.forEach(function (t) {
      t.addEventListener('click', function () {
        var g = t.getAttribute('data-cs-group');
        tabs.forEach(function (o) { var on = o === t; o.classList.toggle('is-active', on); o.setAttribute('aria-pressed', on ? 'true' : 'false'); });
        cards.forEach(function (c) {
          var show = g === 'all' || c.getAttribute('data-cs-card') === g;
          if (show && c.hidden) { c.hidden = false; c.classList.remove('is-in'); void c.offsetWidth; c.classList.add('is-in'); }
          else if (!show) c.hidden = true;
        });
      });
    });
  });

  if (!root) return;
  var bundle = qs('[data-cs-bundle]', root);
  if (!bundle) return;
  var tierInputs = qsa('input[data-bra-tier]', bundle);
  var errEl = qs('[data-bra-err]', bundle);
  var addBtn = qs('[data-bra-add]', bundle);

  /* ---- The colour block above the packs (the Dab & Go selector) ----
     One tap on a swatch puts that colour on every pair of every pack, names
     it in the label, shows what it suits and moves the gallery to its photo.
     "Different colours per pair" opens a colour row per pair (chips drawn by
     bra.js, painted here with the swatch photos). */
  var shadeBlock = qs('[data-cs-shade]', root);
  var swatches = shadeBlock ? qsa('[data-cs-swatch]', shadeBlock) : [];
  var shadeCurrent = shadeBlock ? qs('[data-cs-shade-current]', shadeBlock) : null;
  var shadeHint = shadeBlock ? qs('[data-cs-shade-hint]', shadeBlock) : null;
  var mixBox = qs('[data-cs-mix]', bundle);
  var globalColour = null;

  function labelOf(value) {
    var sw = swatches.filter(function (s) { return s.getAttribute('data-value') === value; })[0];
    return sw ? sw.getAttribute('data-label') : value;
  }
  function paintPickName(sel) {
    var pick = sel.closest('[data-bra-pick]');
    var name = pick ? qs('[data-cs-pick-name]', pick) : null;
    if (name) name.textContent = labelOf(sel.value);
  }
  qsa('select[data-bra-color]', bundle).forEach(function (sel) {
    var chips = sel.parentNode.querySelector('.brachips');
    if (chips) qsa('.brachips__opt', chips).forEach(function (btn) {
      var opt = Array.prototype.filter.call(sel.options, function (o) { return o.value === btn.getAttribute('data-value'); })[0];
      var dot = qs('.brachips__dot', btn);
      if (opt && dot) {
        var hex = opt.getAttribute('data-hex') || '#ccc';
        dot.style.background = opt.getAttribute('data-img') ? hex + ' url(' + opt.getAttribute('data-img') + ') center / cover no-repeat' : hex;
      }
      var lab = btn.querySelector('.brachips__label, span:not(.brachips__dot)');
      if (opt && lab && opt.getAttribute('data-label')) lab.textContent = opt.getAttribute('data-label');
    });
    paintPickName(sel);
  });
  function paintSwatches(value) {
    swatches.forEach(function (sw) {
      var on = sw.getAttribute('data-value') === value;
      sw.classList.toggle('is-active', on);
      sw.setAttribute('aria-checked', on ? 'true' : 'false');
      sw.tabIndex = on ? 0 : -1;
      if (on && shadeHint) shadeHint.textContent = sw.getAttribute('data-hint') || '';
    });
    if (shadeCurrent) shadeCurrent.textContent = labelOf(value);
  }
  // Colour -> gallery position (1-based), from the swatches or, without
  // them, from the bundle's data-colour-slides ("Svart::4;;Beige::5;;…").
  var slideOf = {};
  (bundle.getAttribute('data-colour-slides') || '').split(';;').forEach(function (pair) {
    var kv = pair.split('::'); if (kv.length === 2) slideOf[kv[0]] = parseInt(kv[1], 10);
  });
  function showColourPhoto(value) {
    var sw = swatches.filter(function (s) { return s.getAttribute('data-value') === value; })[0];
    var pos = sw ? parseInt(sw.getAttribute('data-slide'), 10) : (slideOf[value] || NaN);
    if (isNaN(pos) || pos < 1 || typeof window.LovelleShowSlide !== 'function') return;
    window.LovelleShowSlide(pos - 1);
  }
  function setAllColour(value, photo) {
    globalColour = value;
    syncing = true;
    qsa('select[data-bra-color]', bundle).forEach(function (s) { setSelect(s, value); paintPickName(s); });
    syncing = false;
    paintSwatches(value);
    if (photo) showColourPhoto(value);
  }
  swatches.forEach(function (sw, i) {
    sw.addEventListener('click', function () { setAllColour(sw.getAttribute('data-value'), true); });
    sw.addEventListener('keydown', function (e) {
      var step = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : (e.key === 'ArrowLeft' || e.key === 'ArrowUp') ? -1 : 0;
      if (!step) return;
      e.preventDefault();
      var next = swatches[(i + step + swatches.length) % swatches.length];
      next.focus(); next.click();
    });
  });
  if (swatches.length) {
    var startSw = swatches.filter(function (s) { return s.classList.contains('is-active'); })[0] || swatches[0];
    setAllColour(startSw.getAttribute('data-value'), false);
  }
  if (mixBox) mixBox.addEventListener('change', function () {
    bundle.classList.toggle('is-mixing', mixBox.checked);
    if (!mixBox.checked && globalColour) setAllColour(globalColour, false);
  });
  bundle.addEventListener('change', function (e) {
    if (e.target && e.target.matches && e.target.matches('select[data-bra-color]')) paintPickName(e.target);
  });

  function selectedBox() {
    var cur = qs('input[data-bra-tier]:checked', bundle);
    return cur ? cur.closest('[data-bra-tier-box]') : null;
  }
  function setSelect(sel, value) {
    if (!sel || sel.value === value) return;
    if (sel.__bra) sel.__bra.set(value, true); else sel.value = value;
  }

  /* ---- Keep pair N the same in every pack (switching pack keeps her choices) ---- */
  var syncing = false;
  function mirror(src) {
    if (syncing) return;
    var pick = src.closest('[data-bra-pick]');
    if (!pick) return;
    var i = pick.getAttribute('data-index');
    var isSize = src.hasAttribute('data-bra-size');
    syncing = true;
    qsa('[data-bra-pick][data-index="' + i + '"] select[' + (isSize ? 'data-bra-size' : 'data-bra-color') + ']', bundle).forEach(function (s) {
      if (s !== src) setSelect(s, src.value);
    });
    // The first size she picks goes on every pair that has none yet.
    if (isSize && src.value) {
      qsa('select[data-bra-size]', bundle).forEach(function (s) { if (!s.value) setSelect(s, src.value); });
    }
    syncing = false;
    paintMissing();
    paintAllSizes();
  }

  /* ---- Size dropdowns: a readable "Välj storlek" until a size is chosen ----
     bra.js draws the button from the select's value, so an empty value would
     leave a blank box and list the placeholder as a struck-out choice. The
     placeholder row is hidden from the menu and painted on the button instead. */
  function paintSizePlaceholder(sel) {
    var wrap = sel.nextElementSibling;
    if (!wrap || !wrap.classList || !wrap.classList.contains('brasel')) return;
    var ph = qs('.brasel__opt[data-value=""]', wrap);
    if (ph) ph.hidden = true;
    if (sel.value) { wrap.classList.remove('is-empty'); return; }
    var btn = qs('.brasel__btn', wrap);
    var text = sel.options[0] ? sel.options[0].textContent : '';
    if (btn) btn.innerHTML = '<span class="brasel__cur"><span class="brasel__ph">' + text.replace(/[<>&]/g, '') + '</span></span><span class="brasel__caret"></span>';
    wrap.classList.add('is-empty');
  }
  function paintAllSizes() { qsa('select[data-bra-size]', bundle).forEach(paintSizePlaceholder); }
  paintAllSizes();

  /* ---- Design dropdowns: clean thumbnails of the whole pair ----
     bra.js draws its chips from the variant photo (crossed legs, zoomed in);
     every option carries its own small square photo (data-swatch, the front
     shot of that design), which replaces the chip in the button and the menu. */
  var swatchOf = {};
  qsa('select[data-bra-color] option', bundle).forEach(function (o) { if (o.getAttribute('data-swatch')) swatchOf[o.value] = o.getAttribute('data-swatch'); });
  function paintChips(wrap, sel) {
    qsa('.brasel__opt', wrap).forEach(function (o) {
      var i = qs('.brasel__sw', o); var u = swatchOf[o.getAttribute('data-value')];
      if (i && u) { i.className = 'brasel__sw brasel__sw--photo'; i.style.backgroundImage = 'url(' + u + ')'; }
    });
    var cur = qs('.brasel__btn .brasel__sw', wrap); var u2 = swatchOf[sel.value];
    if (cur && u2 && cur.style.backgroundImage.indexOf(u2) < 0) { cur.className = 'brasel__sw brasel__sw--photo'; cur.style.backgroundImage = 'url(' + u2 + ')'; }
  }
  qsa('select[data-bra-color]', bundle).forEach(function (sel) {
    var wrap = sel.nextElementSibling;
    if (!wrap || !wrap.classList || !wrap.classList.contains('brasel')) return;
    paintChips(wrap, sel);
    var btn = qs('.brasel__btn', wrap);
    if (btn && 'MutationObserver' in window) new MutationObserver(function () { paintChips(wrap, sel); }).observe(btn, { childList: true });
  });

  function paintMissing() {
    qsa('[data-bra-pick]', bundle).forEach(function (p) {
      var s = qs('select[data-bra-size]', p);
      p.classList.toggle('is-nosize', !!s && !s.value);
    });
  }

  bundle.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.matches) return;
    if (t.matches('select[data-bra-size], select[data-bra-color]')) {
      mirror(t);
      if (errEl && t.matches('select[data-bra-size]')) errEl.hidden = true;
      return;
    }
    if (t.matches('input[data-bra-tier]')) { paintSticky(t, true); }
  });

  /* ---- Ask for a missing size before bra.js adds anything ---- */
  function firstMissing() {
    var box = selectedBox();
    if (!box) return null;
    return qsa('[data-bra-pick]', box).filter(function (p) {
      var s = qs('select[data-bra-size]', p);
      return s && !s.value;
    })[0] || null;
  }
  function askForSize(pick) {
    if (errEl) {
      errEl.textContent = (bundle.getAttribute('data-msg-size') || '').replace('[n]', pick.getAttribute('data-index'));
      errEl.hidden = false;
    }
    pick.classList.remove('is-flash'); void pick.offsetWidth; pick.classList.add('is-flash');
    scrollToEl(pick);
    var btn = qs('[data-bra-pick-size] .brasel__btn', pick) || qs('select[data-bra-size]', pick);
    if (btn) setTimeout(function () { btn.focus({ preventScroll: true }); }, reduced ? 0 : 450);
  }
  bundle.addEventListener('click', function (e) {
    var hit = e.target && e.target.closest ? e.target.closest('[data-bra-add]') : null;
    if (!hit) return;
    var miss = firstMissing();
    if (!miss) return;
    e.preventDefault();
    e.stopPropagation();
    askForSize(miss);
  }, true);

  /* ---- The sticky bar follows the chosen pack ---- */
  var sticky = qs('[data-cs-sticky]', root);
  var stickyName = sticky ? qs('[data-cs-sticky-name]', sticky) : null;
  var stickyTotal = sticky ? qs('[data-cs-sticky-total]', sticky) : null;
  var stickyBuy = sticky ? qs('[data-cs-sticky-buy]', sticky) : null;
  function bump(el) {
    if (!el || reduced) return;
    el.classList.remove('is-bump'); void el.offsetWidth; el.classList.add('is-bump');
  }
  function paintSticky(input, animate) {
    if (!input) return;
    if (stickyName) stickyName.textContent = input.getAttribute('data-name') || stickyName.textContent;
    if (stickyTotal) stickyTotal.textContent = input.getAttribute('data-total') || stickyTotal.textContent;
    if (animate) { bump(stickyTotal); bump(qs('[data-bra-total]', bundle)); }
  }
  paintSticky(qs('input[data-bra-tier]:checked', bundle), false);
  if (stickyBuy) stickyBuy.addEventListener('click', function () {
    var miss = firstMissing();
    if (miss) { askForSize(miss); return; }
    if (addBtn) addBtn.click();
  });

  /* ---- "Välj [design]" cards further down: that design on every pair ---- */
  qsa('[data-cs-pick-colour]').forEach(function (b) {
    b.addEventListener('click', function () {
      var value = b.getAttribute('data-value');
      if (!value) return;
      if (mixBox && mixBox.checked) { mixBox.checked = false; bundle.classList.remove('is-mixing'); }
      setAllColour(value, true);
      var target = shadeBlock || selectedBox() || bundle;
      scrollToEl(target);
      flash(target);
    });
  });

  /* The size guide's answer goes on every pair. */
  window.CosySetSize = function (s) {
    syncing = true;
    qsa('select[data-bra-size]', bundle).forEach(function (sel) {
      var opt = Array.prototype.filter.call(sel.options, function (o) { return o.value.toLowerCase() === String(s).toLowerCase(); })[0];
      if (opt) setSelect(sel, opt.value);
    });
    syncing = false;
    if (errEl) errEl.hidden = true;
    paintMissing();
    paintAllSizes();
  };

  /* Open the gallery on the starting design (the first one in the dropdown order). */
  var startSlide = parseInt(bundle.getAttribute('data-cs-start-slide'), 10) || 0;
  if (startSlide > 0 && typeof window.LovelleShowSlide === 'function') {
    window.LovelleShowSlide(startSlide);
    var csThumbs = document.querySelector('[data-cs-thumbs]');
    var activeThumb = csThumbs ? csThumbs.querySelector('button.is-active') : null;
    if (activeThumb && csThumbs.scrollTo) csThumbs.scrollTo({ left: activeThumb.offsetLeft - 8, behavior: 'auto' });
  }

  paintMissing();
  if (!tierInputs.length) return;
})();
